import { useEffect, useState } from "react";
import { Autocomplete, TextField, Chip, Box, Typography, Stack } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import getApiData from "../../../../../hooks/apiRequests/getRequest";
import { useToast } from "../../../../shared/toastMessages/toastProvider";

const MultiSearchSelector = ({ value = [], onChange, error = "", disabled = false }) => {
  const [mailingList, setMailingList] = useState([]);
  const { showToast } = useToast();

  useEffect(() => {
    const fetchLists = async () => {
      try {
        const res = await getApiData("/Emailling/Create/List");

        const mapped = (res?.data || []).map((list) => ({
          label: list.item,
          id: list.id,
        }));

        setMailingList(mapped);
      } catch (err) {
        console.error(err);
        showToast("Failed to load mailing lists.", "error");
      }
    };

    fetchLists();
  }, [showToast]);

  const selectedOptions = mailingList.filter((m) =>
    value.some((v) => v.id === m.id)
  );

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <GroupsOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Mailing lists
        </Typography>
      </Stack>

      <Autocomplete
        multiple
        disabled={disabled}
        options={mailingList}
        value={selectedOptions}
        onChange={(event, newValue) => {
          if (disabled || !onChange) return;
          const idsOnly = newValue.map((item) => ({ id: item.id }));
          onChange(idsOnly);
        }}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        filterSelectedOptions
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => {
            const tagProps = getTagProps({ index });
            if (disabled) {
              const { onDelete, ...rest } = tagProps;
              return (
                <Chip
                  key={option.id}
                  label={option.label}
                  size="small"
                  {...rest}
                  sx={{ borderRadius: "8px", fontWeight: 500 }}
                />
              );
            }
            return (
              <Chip
                key={option.id}
                label={option.label}
                size="small"
                {...tagProps}
                sx={{ borderRadius: "8px", fontWeight: 500 }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={disabled ? "Mailing lists" : "Search and select"}
            placeholder={
              disabled
                ? selectedOptions.length
                  ? ""
                  : "No mailing lists"
                : selectedOptions.length
                  ? ""
                  : "Start typing..."
            }
            error={Boolean(error)}
            helperText={
              error ||
              (disabled
                ? selectedOptions.length
                  ? `${selectedOptions.length} list${selectedOptions.length === 1 ? "" : "s"}`
                  : " "
                : "Select at least one mailing list")
            }
            size="small"
            InputProps={{
              ...params.InputProps,
              readOnly: disabled,
            }}
          />
        )}
      />

      {!disabled && value.length > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {value.length} list{value.length === 1 ? "" : "s"} selected
        </Typography>
      ) : null}
    </Box>
  );
};

export default MultiSearchSelector;
